import classNames from 'classnames';
import React, { Component } from 'react';
import { NumericFormat } from 'react-number-format';
import type { NumberFormatValues, SourceInfo } from 'react-number-format';

import { observer } from 'mobx-react';

import { bind } from 'bind-decorator';

import type { IFieldProps } from 'bz-core-controls/controls/form/field/field/IFieldProps';

import { DATA_TYPE, ICON_POSITION } from 'click-controls/constant/enums';

import ClickFieldError from '../common/error';
import ClickFieldInputContainer from '../common/input-container';
import ClickFieldRequired from '../common/required';
import FieldCurrencyIcon from './controls/currency-icon';

import { ConfigObservable } from 'bz-stores/lib/config';

export const convertTypeOfValue = (
  value: string | number | null | undefined,
  convertType?: DATA_TYPE
) => {
  switch (convertType) {
    case DATA_TYPE.String:
      return String(value);

    case DATA_TYPE.Number:
      return Number(value);

    default:
      return Number(value);
  }
};

export interface IFieldCurrencyProps extends IFieldProps {
  className?: string;
  convertType?: DATA_TYPE;
  defaultValue?: any;
  errorText?: string;
  showCurrencyIcon?: boolean;
  iconPosition?: ICON_POSITION.Left | ICON_POSITION.Right;
  inputContainerClass?: string;
  label?: string;
  labelClass?: string;
  placeholder?: string;
  prefix?: string;
  required?: boolean;
  scale?: number;
  submitted?: boolean;
  testId?: string;
  value?: string | number | null;
  onHandleChange?: (value: string | number) => void;
}

@observer
class FieldCurrency extends Component<IFieldCurrencyProps> {
  render() {
    const {
      className,
      convertType = DATA_TYPE.Number,
      disabled,
      errorText,
      showCurrencyIcon,
      iconPosition,
      inputContainerClass,
      label,
      labelClass,
      prefix,
      required = false,
      scale = 2,
      submitted,
      testId,
      value,
      onHandleChange,
      ...other
    } = this.props;
    return (
      <div className="field-container">
        <label className={classNames('click-label', labelClass)}>
          <span>
            {label}
            {required && <ClickFieldRequired />}
          </span>
        </label>
        <ClickFieldInputContainer className={inputContainerClass}>
          {showCurrencyIcon && (
            <FieldCurrencyIcon
              currency={ConfigObservable.config.CURRENCY}
              position={iconPosition}
              disabled={disabled}
            />
          )}
          <NumericFormat
            data-testid={testId}
            className={classNames(className, 'click-input', {
              'mb-0': !!errorText && submitted,
              'pl-10': iconPosition == ICON_POSITION.Left && showCurrencyIcon,
              'pr-10': iconPosition == ICON_POSITION.Right && showCurrencyIcon,
              'bg-gray-50': disabled
            })}
            disabled={disabled}
            decimalScale={scale}
            fixedDecimalScale={true}
            isAllowed={this.isAllowed}
            prefix={prefix}
            thousandSeparator={true}
            type="text"
            value={convertTypeOfValue(value, convertType)}
            onValueChange={this.onValueChange}
            {...other}
          />
        </ClickFieldInputContainer>
        {!!errorText && submitted && <ClickFieldError value={errorText} />}
      </div>
    );
  }

  @bind
  private isAllowed({ value }: NumberFormatValues): boolean {
    const { convertType } = this.props;
    const converted = convertTypeOfValue(value, convertType);

    return this.isAllowedMax(value) && this.isAllowedMin(value);
  }

  @bind
  private isAllowedMax(value: string | number): boolean {
    const { max } = this.props;

    return !max || value <= max;
  }

  @bind
  private isAllowedMin(value: string | number): boolean {
    const { min } = this.props;

    return !min || value >= min;
  }

  @bind
  private onValueChange({ value }: NumberFormatValues, sourceInfo: SourceInfo) {
    const { convertType, onHandleChange } = this.props;

    onHandleChange?.(convertTypeOfValue(value, convertType));
  }
}
export default FieldCurrency;
